import { useState, useEffect, useCallback, useRef } from "react";
import { ukDate } from "@/lib/utils";
import { WizardDebugButton } from "@/components/ReportBugDialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Loader2, CheckCircle, User, Banknote, FileText, ArrowRight, ArrowLeft, Plus,
  CalendarIcon, Sparkles, Search, Trash2, Users, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { errorDetailMessage } from "@/lib/errorDetailMessage";

const STEPS = ["Borrowers", "Loan Details", "Review & Create", "Attach Docs"];

const parseDDMMYYYY = (s) => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(s || "");
  return m ? new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1])) : undefined;
};
const toDDMMYYYY = (d) =>
  `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;

const DateField = ({ value, onChange, placeholder = "Pick a date", testid, disabled }) => (
  <Popover>
    <PopoverTrigger asChild>
      <Button variant="outline" className="w-full justify-start font-normal h-9 text-sm" data-testid={testid} disabled={disabled}>
        <CalendarIcon className="w-4 h-4 mr-2 text-slate-400" />
        {value || <span className="text-muted-foreground">{placeholder}</span>}
      </Button>
    </PopoverTrigger>
    <PopoverContent className="w-auto p-0" align="start">
      <Calendar mode="single" selected={parseDDMMYYYY(value)} onSelect={(d) => d && onChange(toDDMMYYYY(d))} initialFocus
        captionLayout="dropdown-buttons" fromYear={1930} toYear={new Date().getFullYear()} />
    </PopoverContent>
  </Popover>
);

const sumsubDobToDDMMYYYY = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || "");
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
};

const localMobile = (s, isAus = false) => {
  const digits = String(s || "").replace(/\D/g, "");
  if (isAus) return digits.startsWith("61") ? `0${digits.slice(2)}` : digits;
  return digits.startsWith("44") ? `0${digits.slice(2)}` : digits;
};

// Per-guarantor Sumsub ID-check sync — searches the deal's regional Sumsub
// workspace (UK or AUS) and fills the card with the verified identity data.
function SumsubSync({ guarantor, onFill, idx, isAus }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);

  const runSearch = async (q) => {
    if (!q || q.trim().length < 2) return;
    try {
      setSearching(true);
      const { data } = await api.get("/contracts/sumsub/search", { params: { q: q.trim(), region: isAus ? "AUS" : "UK" } });
      setResults(data.applicants || []);
    } catch (e) {
      toast.error(errorDetailMessage(e, "Sumsub search failed"));
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const openPanel = () => {
    const name = `${guarantor.borrower_firstname || ""} ${guarantor.borrower_lastname || ""}`.trim() ||
      guarantor.borrower_lastname || "";
    setQuery(name);
    setResults(null);
    setOpen(true);
    if (name.length >= 2) runSearch(name);
  };

  const applyApplicant = (a) => {
    onFill({
      borrower_firstname: a.first_name || guarantor.borrower_firstname,
      borrower_lastname: a.last_name || guarantor.borrower_lastname,
      borrower_dob: sumsubDobToDDMMYYYY(a.dob) || guarantor.borrower_dob,
      borrower_gender: a.gender === "M" ? "Male" : a.gender === "F" ? "Female" : guarantor.borrower_gender,
      borrower_email: a.email || guarantor.borrower_email,
      borrower_mobile: localMobile(a.phone, isAus) || guarantor.borrower_mobile,
      borrower_address: a.street || guarantor.borrower_address,
      borrower_city: a.town || guarantor.borrower_city,
      borrower_province: a.state || guarantor.borrower_province,
      borrower_zipcode: a.post_code || guarantor.borrower_zipcode,
      sumsub_applicant_id: a.applicant_id,
      sumsub_review_answer: a.review_answer,
    });
    setOpen(false);
    toast.success(`Filled from Sumsub ID check (${a.review_answer || "pending"})`);
  };

  return (
    <div>
      {!open ? (
        <div className="flex items-center gap-2">
          {guarantor.sumsub_applicant_id && (
            <span className={`text-xs px-1.5 py-0.5 rounded flex items-center gap-1 ${
              guarantor.sumsub_review_answer === "GREEN" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
            }`} data-testid={`sumsub-synced-badge-${idx}`}>
              <ShieldCheck className="w-3 h-3" /> Sumsub {guarantor.sumsub_review_answer || "synced"}
            </span>
          )}
          <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={openPanel}
            data-testid={`sumsub-sync-btn-${idx}`}>
            <ShieldCheck className="w-3 h-3 mr-1 text-blue-600" /> Sync from Sumsub
          </Button>
        </div>
      ) : (
        <div className="border border-blue-200 bg-blue-50/50 rounded-lg p-2 space-y-2 w-full" data-testid={`sumsub-panel-${idx}`}>
          <div className="flex gap-2">
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, email or phone…"
              className="h-8 text-sm bg-white" onKeyDown={(e) => e.key === "Enter" && runSearch(query)}
              data-testid={`sumsub-search-input-${idx}`} />
            <Button type="button" size="sm" className="h-8" onClick={() => runSearch(query)} disabled={searching}
              data-testid={`sumsub-search-btn-${idx}`}>
              {searching ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
            </Button>
            <Button type="button" size="sm" variant="ghost" className="h-8" onClick={() => setOpen(false)}>✕</Button>
          </div>
          {results !== null && (
            results.length === 0 ? (
              <p className="text-xs text-muted-foreground">No ID checks found — try email or phone.</p>
            ) : (
              <div className="space-y-1 max-h-44 overflow-y-auto">
                {results.map((a) => (
                  <button key={a.applicant_id} type="button" onClick={() => applyApplicant(a)}
                    className="w-full text-left p-2 bg-white border rounded hover:border-blue-400 text-xs space-y-0.5"
                    data-testid={`sumsub-result-${a.applicant_id}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm">{a.full_name}</span>
                      <span className={`px-1.5 py-0.5 rounded font-medium ${
                        a.review_answer === "GREEN" ? "bg-emerald-100 text-emerald-700"
                          : a.review_answer === "RED" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"
                      }`}>
                        {a.review_answer || a.review_status || "pending"}
                      </span>
                    </div>
                    <p className="text-muted-foreground">
                      {[a.dob, a.email, a.phone].filter(Boolean).join(" · ")}
                    </p>
                    {a.address && <p className="text-muted-foreground truncate">{a.address}</p>}
                  </button>
                ))}
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}

// Per-guarantor borrower fields with its own address finder — UK: Ideal
// Postcodes autocomplete; AUS: bundled postcode → suburb/state lookup.
function BorrowerFields({ value, onChange, idx, disabled, isAus }) {
  const [addrQuery, setAddrQuery] = useState("");
  const [addrSuggestions, setAddrSuggestions] = useState([]);
  const [addrSearching, setAddrSearching] = useState(false);
  const skipNextAddrSearch = useRef(false);

  useEffect(() => {
    if (skipNextAddrSearch.current) {
      skipNextAddrSearch.current = false;
      return;
    }
    if (addrQuery.trim().length < 3) {
      setAddrSuggestions([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        setAddrSearching(true);
        if (isAus) {
          const digits = addrQuery.replace(/\D/g, "");
          if (digits.length !== 4) { setAddrSuggestions([]); return; }
          const { data } = await api.get("/aus/postcode-suburbs", { params: { postcode: digits } });
          setAddrSuggestions(data.ok
            ? (data.suburbs || []).map((s) => ({ id: s.label, address: s.label, aus: s }))
            : []);
          if (!data.ok && data.error) toast.error(data.error);
        } else {
          const { data } = await api.get("/legal/docs/postcode-lookup", { params: { term: addrQuery } });
          setAddrSuggestions(data.ok ? data.suggestions || [] : []);
          if (!data.ok && data.error) toast.error(data.error);
        }
      } catch {
        setAddrSuggestions([]);
      } finally {
        setAddrSearching(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [addrQuery, isAus]);

  const selectAddress = async (s) => {
    setAddrSuggestions([]);
    skipNextAddrSearch.current = true;
    setAddrQuery(s.address);
    if (s.aus) {
      onChange("borrower_city", s.aus.suburb || "");
      onChange("borrower_province", s.aus.state || "");
      onChange("borrower_zipcode", s.aus.postcode || "");
      toast.success("Suburb filled in — type the street address below");
      return;
    }
    try {
      const { data } = await api.get(`/legal/docs/postcode-lookup/${s.id}`);
      if (data.ok) {
        onChange("borrower_address", data.line || "");
        onChange("borrower_city", data.city || "");
        onChange("borrower_province", data.county || "");
        onChange("borrower_zipcode", data.postcode || "");
        toast.success("Address filled in");
      } else if (data.error) {
        toast.error(data.error);
      }
    } catch {
      toast.error("Address lookup failed");
    }
  };

  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="col-span-2">
        <Label className="text-xs text-muted-foreground">Business Name *</Label>
        <Input value={value.borrower_business_name || ""} onChange={(e) => onChange("borrower_business_name", e.target.value)}
          disabled={disabled} data-testid={`borrower-business-name-${idx}`} />
      </div>
      <div>
        <Label className="text-xs text-muted-foreground">{isAus ? "ABN / ACN" : "Company Number"}</Label>
        <Input value={value.borrower_unique_number || ""} onChange={(e) => onChange("borrower_unique_number", e.target.value)}
          disabled={disabled} data-testid={`borrower-company-number-${idx}`} />
      </div>
      <div>
        <Label className="text-xs text-muted-foreground">Title</Label>
        <Select value={value.borrower_title || ""} onValueChange={(v) => onChange("borrower_title", v)} disabled={disabled}>
          <SelectTrigger data-testid={`borrower-title-${idx}`}><SelectValue placeholder="—" /></SelectTrigger>
          <SelectContent>
            {["Mr.", "Mrs.", "Miss", "Ms.", "Dr."].map((t) => (
              <SelectItem key={t} value={t}>{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs text-muted-foreground">First Name</Label>
        <Input value={value.borrower_firstname || ""} onChange={(e) => onChange("borrower_firstname", e.target.value)}
          disabled={disabled} data-testid={`borrower-firstname-${idx}`} />
      </div>
      <div>
        <Label className="text-xs text-muted-foreground">Last Name</Label>
        <Input value={value.borrower_lastname || ""} onChange={(e) => onChange("borrower_lastname", e.target.value)}
          disabled={disabled} data-testid={`borrower-lastname-${idx}`} />
      </div>
      <div>
        <Label className="text-xs text-muted-foreground">Date of Birth (optional)</Label>
        <DateField value={value.borrower_dob || ""} onChange={(v) => onChange("borrower_dob", v)}
          placeholder="dd/mm/yyyy" testid={`borrower-dob-${idx}`} disabled={disabled} />
      </div>
      <div>
        <Label className="text-xs text-muted-foreground">Gender (optional)</Label>
        <Select value={value.borrower_gender || ""} onValueChange={(v) => onChange("borrower_gender", v)} disabled={disabled}>
          <SelectTrigger data-testid={`borrower-gender-${idx}`}><SelectValue placeholder="—" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="Male">Male</SelectItem>
            <SelectItem value="Female">Female</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs text-muted-foreground">Email</Label>
        <Input value={value.borrower_email || ""} onChange={(e) => onChange("borrower_email", e.target.value)}
          disabled={disabled} data-testid={`borrower-email-${idx}`} />
      </div>
      <div>
        <Label className="text-xs text-muted-foreground">Mobile</Label>
        <Input value={value.borrower_mobile || ""} onChange={(e) => onChange("borrower_mobile", e.target.value)}
          disabled={disabled} data-testid={`borrower-mobile-${idx}`} />
      </div>
      <div className="col-span-2 relative">
        <Label className="text-xs text-muted-foreground flex items-center gap-1">
          <Search className="w-3 h-3" /> {isAus ? "Address Finder — type the 4-digit postcode, pick the suburb" : "Address Finder — type a postcode or address"}
        </Label>
        <div className="relative">
          <Input value={addrQuery} onChange={(e) => setAddrQuery(e.target.value)} disabled={disabled}
            placeholder={isAus ? "e.g. 2000" : "e.g. SW1A 1AA or 10 Downing Street"} data-testid={`borrower-address-search-${idx}`} />
          {addrSearching && <Loader2 className="absolute right-2 top-2.5 w-4 h-4 animate-spin text-slate-400" />}
        </div>
        {addrSuggestions.length > 0 && (
          <div className="absolute z-50 mt-1 w-full bg-white border rounded-md shadow-lg max-h-52 overflow-y-auto"
            data-testid={`borrower-address-suggestions-${idx}`}>
            {addrSuggestions.map((s) => (
              <button key={s.id} type="button"
                className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 border-b last:border-b-0"
                onClick={() => selectAddress(s)} data-testid={`addr-suggestion-${s.id}`}>
                {s.address}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="col-span-2">
        <Label className="text-xs text-muted-foreground">Address</Label>
        <Input value={value.borrower_address || ""} onChange={(e) => onChange("borrower_address", e.target.value)}
          disabled={disabled} data-testid={`borrower-address-${idx}`} />
      </div>
      <div>
        <Label className="text-xs text-muted-foreground">{isAus ? "City / Suburb" : "City / Town"}</Label>
        <Input value={value.borrower_city || ""} onChange={(e) => onChange("borrower_city", e.target.value)}
          disabled={disabled} data-testid={`borrower-city-${idx}`} />
      </div>
      <div>
        <Label className="text-xs text-muted-foreground">{isAus ? "State" : "County"}</Label>
        <Input value={value.borrower_province || ""} onChange={(e) => onChange("borrower_province", e.target.value)}
          disabled={disabled} data-testid={`borrower-county-${idx}`} />
      </div>
      <div>
        <Label className="text-xs text-muted-foreground">Postcode</Label>
        <Input value={value.borrower_zipcode || ""} onChange={(e) => onChange("borrower_zipcode", e.target.value)}
          disabled={disabled} data-testid={`borrower-postcode-${idx}`} />
      </div>
    </div>
  );
}

// Pop-out wizard: create the guarantor borrowers + loan directly in LoanDisk
// with max autofill from the deal, then attach the completed DocuSign files.
const YesNo = ({ value, onChange, testid }) => (
  <div className="inline-flex rounded-md border overflow-hidden text-xs" data-testid={testid}>
    {[["Yes", true], ["No", false]].map(([lbl, v]) => (
      <button key={lbl} type="button" onClick={() => onChange(v)} data-testid={`${testid}-${lbl.toLowerCase()}`}
        className={`px-3 py-1.5 transition-colors ${value === v ? "bg-slate-900 text-white" : "bg-white hover:bg-slate-50"}`}>{lbl}</button>
    ))}
  </div>
);

const ProductBasisBlock = ({ basis, loan, setL, products }) => {
  const d = basis?.detail || {};
  const label = products.find((p) => p.ho === loan.home_owner && p.deb === loan.debenture)?.label;
  const unknownHo = typeof loan.home_owner !== "boolean";
  return (
    <div className={`rounded-lg border p-3 space-y-2 ${unknownHo ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-slate-50"}`} data-testid="product-basis-block">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-xs font-semibold">Loan Product *</div>
          <div className="text-sm font-mono" data-testid="product-derived-label">{label || "— confirm Homeowner and Debenture —"}</div>
        </div>
        <div className="flex items-center gap-4 text-xs">
          <div className="space-y-1">
            <div className="text-muted-foreground">Homeowner</div>
            <YesNo value={loan.home_owner} onChange={(v) => setL("home_owner", v)} testid="product-homeowner" />
          </div>
          <div className="space-y-1">
            <div className="text-muted-foreground">Debenture</div>
            <YesNo value={loan.debenture} onChange={(v) => setL("debenture", v)} testid="product-debenture" />
          </div>
        </div>
      </div>
      <div className="text-[11px] text-muted-foreground space-y-0.5" data-testid="product-evidence">
        <div>Homeowner: {basis?.ho_source ? <>pre-filled from <b>{basis.ho_source}</b></> : <span className="text-amber-700 font-medium">not found in the UW note or Monday — pick it manually</span>}
          {d.uw_note?.url && <> · <a href={d.uw_note.url} target="_blank" rel="noreferrer" className="underline">UW note</a></>}</div>
        <div>Debenture: {basis?.deb_source ? <>from <b>{basis.deb_source}</b></> : "no evidence"}
          {d.offer?.security && <> · offer SECURITY: “{d.offer.security}”</>}
          {d.contract_wizard_debenture != null && <> · contract wizard: {d.contract_wizard_debenture ? "Debenture generated" : "no Debenture"}</>}</div>
        {(basis?.conflicts || []).map((c, i) => <div key={i} className="text-red-700">Conflict: {c}</div>)}
      </div>
    </div>
  );
};


export default function LoanDiskCreateWizard({ application, open, onOpenChange, onCreated }) {
  const appId = application.application_id || application.id;
  const isAus = application?.currency === "AUD" || ["AUS", "AU"].includes((application?.country || "").toUpperCase());
  const cur = isAus ? "A$" : "£";
  const [step, setStep] = useState(0);
  const [prefill, setPrefill] = useState(null);
  const [loadingPrefill, setLoadingPrefill] = useState(false);
  const [busy, setBusy] = useState(null);

  const [borrowerId, setBorrowerId] = useState(null);
  const [borrowerMode, setBorrowerMode] = useState("new"); // new | existing
  const [guarantors, setGuarantors] = useState([]);
  const [mainIdx, setMainIdx] = useState(0);
  const [loan, setLoan] = useState({});
  const [createdLoanId, setCreatedLoanId] = useState(null);
  const [selectedEnvelopes, setSelectedEnvelopes] = useState([]);
  const [attachResults, setAttachResults] = useState(null);
  const [duplicateLoan, setDuplicateLoan] = useState(null);

  const loadPrefill = useCallback(async () => {
    try {
      setLoadingPrefill(true);
      const { data } = await api.get(`/loandisk-direct/applications/${appId}/prefill`);
      setPrefill(data);
      const gs = (data.guarantors_prefill || []).length > 0
        ? data.guarantors_prefill
        : [data.borrower_prefill || {}];
      setGuarantors(gs);
      setMainIdx(0);
      setLoan(data.loan_prefill || {});
      setSelectedEnvelopes((data.envelopes || []).map((e) => e.envelope_id));
      setDuplicateLoan(data.loan_in_loandisk || null);
      if (data.existing_borrower_id) {
        setBorrowerId(data.existing_borrower_id);
        setBorrowerMode("existing");
      } else if ((data.existing_borrower_matches || []).length > 0) {
        setBorrowerMode("existing");
      }
    } catch (e) {
      toast.error(errorDetailMessage(e, "Failed to load autofill data"));
    } finally {
      setLoadingPrefill(false);
    }
  }, [appId]);

  useEffect(() => {
    if (open) {
      setStep(0);
      setCreatedLoanId(null);
      setAttachResults(null);
      setDuplicateLoan(null);
      loadPrefill();
    }
  }, [open, loadPrefill]);

  const linkExistingLoan = async () => {
    try {
      setBusy("link");
      const { data } = await api.post(`/loandisk-direct/applications/${appId}/link-existing`, {
        loan_id: duplicateLoan?.loan_id ? String(duplicateLoan.loan_id) : null,
        loan_application_id: duplicateLoan?.loan_application_id || null,
      });
      setCreatedLoanId(data.loan_id);
      setDuplicateLoan(null);
      toast.success(`Linked existing LoanDisk loan ${data.loan_id}`);
      setStep(3);
    } catch (e) {
      toast.error(errorDetailMessage(e, "Failed to link existing loan"));
    } finally {
      setBusy(null);
    }
  };

  const setG = (i, k, v) => setGuarantors((prev) => prev.map((g, gi) => (gi === i ? { ...g, [k]: v } : g)));
  const fillG = (i, patch) => setGuarantors((prev) => prev.map((g, gi) => (gi === i ? { ...g, ...patch } : g)));

  const addGuarantor = () => setGuarantors((prev) => [...prev, {
    borrower_business_name: prefill?.borrower_prefill?.borrower_business_name || "",
    borrower_unique_number: prefill?.borrower_prefill?.borrower_unique_number || "",
  }]);

  const removeGuarantor = (i) => {
    setGuarantors((prev) => prev.filter((_, gi) => gi !== i));
    setMainIdx((prev) => (i < prev ? prev - 1 : i === prev ? 0 : prev));
  };

  const rowsToCreate = guarantors.filter((g) => !g.borrower_id);
  const usingExistingMain = borrowerMode === "existing" && !!borrowerId;

  const createBorrowers = async () => {
    if (rowsToCreate.length === 0) {
      if (borrowerId) setStep(1);
      else toast.error("Select or create a main borrower first");
      return;
    }
    try {
      setBusy("borrower");
      const toCreate = guarantors
        .map((g, i) => ({ ...g, is_main: !usingExistingMain && i === mainIdx }))
        .filter((g) => !g.borrower_id);
      const { data } = await api.post(`/loandisk-direct/applications/${appId}/create-borrowers`, {
        borrowers: toCreate,
        existing_main_borrower_id: usingExistingMain ? parseInt(borrowerId) : null,
      });
      // Merge per-row results back into the cards
      let ri = 0;
      setGuarantors((prev) => prev.map((g) => {
        if (g.borrower_id) return g;
        const r = data.results[ri++];
        if (r?.borrower_id) return { ...g, borrower_id: r.borrower_id, error: null };
        return { ...g, error: r?.error || "Failed" };
      }));
      (data.results || []).filter((r) => r.warning).forEach((r) =>
        toast.info(r.warning, { duration: 10000 })
      );
      if (data.success) {
        setBorrowerId(data.main_borrower_id);
        toast.success(`${data.results.length} borrower(s) created in LoanDisk`);
        setStep(1);
      } else {
        if (data.main_borrower_id) setBorrowerId(data.main_borrower_id);
        toast.error("Some borrowers failed — check the errors and retry");
      }
    } catch (e) {
      toast.error(errorDetailMessage(e, "Failed to create borrowers"));
    } finally {
      setBusy(null);
    }
  };

  const createLoan = async () => {
    try {
      setBusy("loan");
      const { data } = await api.post(`/loandisk-direct/applications/${appId}/create-loan`, {
        ...loan,
        borrower_id: parseInt(borrowerId),
        loan_product_id: null,
        home_owner: loan.home_owner,
        debenture: loan.debenture,
        loan_disbursed_by_id: parseInt(loan.loan_disbursed_by_id),
        loan_payment_scheme_id: parseInt(loan.loan_payment_scheme_id),
        loan_principal_amount: parseFloat(loan.loan_principal_amount),
        loan_interest: parseFloat(loan.loan_interest),
        loan_duration: parseInt(loan.loan_duration),
        loan_num_of_repayments: parseInt(loan.loan_num_of_repayments),
        admin_fee: loan.admin_fee ? parseFloat(loan.admin_fee) : null,
        legal_fee: loan.legal_fee ? parseFloat(loan.legal_fee) : null,
        loan_first_repayment_date: loan.loan_first_repayment_date || null,
      });
      setCreatedLoanId(data.loan_id);
      toast.success(`Loan ${data.loan_id} created in LoanDisk 🎉`);
      setStep(3);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.duplicate_loan) {
        setDuplicateLoan({
          ...(detail.existing_loan || {}),
          loan_application_id: detail.existing_loan?.loan_application_id || loan.loan_application_id,
          suggested: detail.suggested_loan_number,
        });
        setStep(0);
        toast.warning("A loan with this Loan # already exists — see options above");
        return;
      }
      // Network/gateway failure: the loan may still have been created —
      // re-check before showing an error so a retry can't duplicate it.
      // 424 is in the test because an upstream failure now answers 424 rather
      // than a 5xx; without it this re-check stops running and a retry creates
      // a second loan.
      if (!e.response || e.response.status >= 500 || e.response.status === 424) {
        try {
          const { data: check } = await api.get(`/loandisk-direct/applications/${appId}/prefill`);
          if (check.existing_loan_id) {
            setCreatedLoanId(check.existing_loan_id);
            toast.success(`Loan ${check.existing_loan_id} was created successfully`);
            setStep(3);
            return;
          }
        } catch { /* fall through to the error toast */ }
      }
      toast.error(errorDetailMessage(e, "Failed to create loan"));
    } finally {
      setBusy(null);
    }
  };

  const attachDocs = async () => {
    try {
      setBusy("docs");
      const { data } = await api.post(`/loandisk-direct/applications/${appId}/attach-docs`, {
        loan_id: String(createdLoanId),
        envelope_ids: selectedEnvelopes,
      });
      setAttachResults(data.results);
      if (data.success) toast.success("DocuSign files attached to the LoanDisk loan");
      else toast.warning("Some files failed to attach — see details");
    } catch (e) {
      toast.error(errorDetailMessage(e, "Failed to attach documents"));
    } finally {
      setBusy(null);
    }
  };

  const finish = () => {
    onOpenChange(false);
    onCreated?.();
  };

  const productLabel = (products, ho, deb) => (products || []).find((p) => p.ho === ho && p.deb === deb)?.label;
  const loanFieldsValid = loan.loan_principal_amount > 0 && loan.loan_duration > 0 &&
    typeof loan.home_owner === "boolean" && typeof loan.debenture === "boolean" &&
    loan.loan_num_of_repayments > 0 && loan.loan_interest !== "" && loan.loan_interest != null &&
    /^\d{2}\/\d{2}\/\d{4}$/.test(loan.loan_released_date || "") &&
    /^\d{2}\/\d{2}\/\d{4}$/.test(loan.loan_first_repayment_date || "");

  const setL = (k, v) => setLoan((p) => ({ ...p, [k]: v }));

  const guarantorCount = guarantors.length + ((prefill?.guarantor_borrowers || []).length || 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" aria-describedby={undefined} data-testid="loandisk-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Banknote className="w-5 h-5 text-emerald-600" />
            Add Loan to LoanDisk — {appId}
            <WizardDebugButton className="ml-auto mr-6" />
          </DialogTitle>
          {prefill?.autofill_source === "monday_offer" && (
            <p className="text-xs text-emerald-700 flex items-center gap-1" data-testid="autofill-source-note">
              <Sparkles className="w-3 h-3" /> Autofilled from the Monday offer — usually only the payment start date is needed.
            </p>
          )}
        </DialogHeader>

        {/* Step indicator */}
        <div className="flex items-center gap-1 mb-2">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-1 flex-1">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                i < step ? "bg-emerald-500 text-white" : i === step ? "bg-blue-600 text-white" : "bg-slate-200 text-slate-500"
              }`}>
                {i < step ? "✓" : i + 1}
              </div>
              <span className={`text-xs ${i === step ? "font-semibold text-slate-800" : "text-muted-foreground"}`}>{s}</span>
            </div>
          ))}
        </div>

        {loadingPrefill ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            <span className="ml-2 text-sm text-muted-foreground">Autofilling from deal data…</span>
          </div>
        ) : (
          <>
            {duplicateLoan && !createdLoanId && (
              <div className="p-3 bg-amber-50 border border-amber-300 rounded-lg space-y-2" data-testid="duplicate-loan-banner">
                <p className="text-sm font-medium text-amber-900">
                  LoanDisk already has a loan with Loan # <span className="font-mono">{duplicateLoan.loan_application_id || loan.loan_application_id || appId}</span>
                </p>
                {duplicateLoan.loan_id ? (
                  <>
                    <p className="text-xs text-amber-800">
                      {duplicateLoan.loan_principal_amount != null && <>{cur}{Number(duplicateLoan.loan_principal_amount).toLocaleString()} · </>}
                      {duplicateLoan.loan_released_date && <>released {ukDate(duplicateLoan.loan_released_date)} · </>}
                      {duplicateLoan.loan_duration && <>{duplicateLoan.loan_duration} {String(duplicateLoan.loan_duration_period || "weeks").toLowerCase()}</>}
                    </p>
                    <Button size="sm" onClick={linkExistingLoan} disabled={busy === "link"} className="bg-amber-600 hover:bg-amber-700"
                      data-testid="link-existing-loan-btn">
                      {busy === "link" ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle className="w-3 h-3 mr-1" />}
                      Link Existing Loan to this Deal
                    </Button>
                  </>
                ) : (
                  <>
                    <p className="text-xs text-amber-800">
                      The clashing loan couldn&apos;t be found in this branch — it may be archived/deleted or in another
                      branch. Adjust the Loan # and retry, or check LoanDisk manually.
                    </p>
                    <Button size="sm" variant="outline" className="border-amber-400 text-amber-800"
                      onClick={() => {
                        setLoan((p) => ({ ...p, loan_application_id: duplicateLoan.suggested || `${p.loan_application_id}-2` }));
                        setDuplicateLoan(null);
                        setStep(1);
                        toast.info("Loan # adjusted — review and retry");
                      }}
                      data-testid="adjust-loan-number-btn">
                      Use {duplicateLoan.suggested || `${loan.loan_application_id || appId}-2`} instead
                    </Button>
                  </>
                )}
              </div>
            )}

            {/* STEP 0: Guarantors → Borrowers */}
            {step === 0 && (
              <div className="space-y-4">
                {prefill?.linked_borrower && (
                  <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg space-y-1" data-testid="linked-borrower-card">
                    <p className="text-sm font-medium text-emerald-900 flex items-center gap-1.5">
                      <CheckCircle className="w-4 h-4" /> A borrower already exists for this deal
                    </p>
                    <p className="text-xs text-emerald-800">
                      {prefill.linked_borrower.business_name || "Borrower on file"}
                      {prefill.linked_borrower.name && <> — {prefill.linked_borrower.name}</>}
                      {prefill.linked_borrower.email && <> · {prefill.linked_borrower.email}</>}
                    </p>
                    {(prefill.guarantor_borrowers || []).length > 0 && (
                      <p className="text-xs text-emerald-800 flex items-center gap-1" data-testid="guarantor-borrowers-note">
                        <Users className="w-3 h-3" />
                        {prefill.guarantor_borrowers.length} borrower record(s) already created for this deal
                        ({prefill.guarantor_borrowers.map((g) => g.name).join(", ")})
                      </p>
                    )}
                    <div className="flex gap-2 pt-1">
                      <Button size="sm" onClick={() => { setBorrowerId(prefill.linked_borrower.borrower_id); setStep(1); }}
                        data-testid="use-linked-borrower-btn">
                        Continue with this borrower <ArrowRight className="w-3 h-3 ml-1" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => { setBorrowerMode("new"); setBorrowerId(null); }}>
                        Create different borrowers
                      </Button>
                    </div>
                  </div>
                )}

                {(prefill?.existing_borrower_matches || []).length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Existing borrower matches in LoanDisk (pick as main)</Label>
                    {prefill.existing_borrower_matches.map((m) => (
                      <label key={m.borrower_id}
                        className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer ${
                          borrowerMode === "existing" && String(borrowerId) === String(m.borrower_id)
                            ? "border-blue-500 bg-blue-50" : "hover:border-slate-300"
                        }`}
                        data-testid={`borrower-match-${m.borrower_id}`}>
                        <div className="flex items-center gap-2">
                          <input type="radio" checked={borrowerMode === "existing" && String(borrowerId) === String(m.borrower_id)}
                            onChange={() => { setBorrowerMode("existing"); setBorrowerId(m.borrower_id); }} />
                          <div>
                            <p className="text-sm font-medium">{m.business_name}</p>
                            <p className="text-xs text-muted-foreground">{m.name} {m.email && `• ${m.email}`}</p>
                          </div>
                        </div>
                        <User className="w-4 h-4 text-slate-400" />
                      </label>
                    ))}
                    <label className="flex items-center gap-2 p-2 cursor-pointer text-sm">
                      <input type="radio" checked={borrowerMode === "new"} onChange={() => { setBorrowerMode("new"); setBorrowerId(null); }}
                        data-testid="borrower-mode-new" />
                      <Plus className="w-3 h-3" /> Create new borrowers instead (main + guarantors below)
                    </label>
                  </div>
                )}

                {(!prefill?.linked_borrower || borrowerMode === "new") && (
                  <div className="space-y-3">
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Sparkles className="w-3 h-3 text-emerald-600" />
                      {prefill?.guarantors_source === "contract_pg"
                        ? "Guarantors pulled from the signed contract pack (Docupilot PG data) — check and edit as needed."
                        : prefill?.guarantors_source === "sumsub_verified"
                          ? "Signers matched to Sumsub-verified ID checks — check and edit as needed."
                          : `Autofilled from ${isAus ? "ABR/ASIC" : "Companies House"} & the deal — check and edit as needed.`}
                    </p>

                    {guarantors.map((g, i) => (
                      <div key={i}
                        className={`border rounded-lg p-3 space-y-3 ${!usingExistingMain && mainIdx === i ? "border-blue-400 bg-blue-50/40" : "bg-white"}`}
                        data-testid={`guarantor-card-${i}`}>
                        <div className="flex items-center justify-between gap-2">
                          <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                            <input type="radio" checked={!usingExistingMain && mainIdx === i}
                              disabled={usingExistingMain || !!g.borrower_id}
                              onChange={() => setMainIdx(i)} data-testid={`guarantor-main-${i}`} />
                            {usingExistingMain
                              ? `Additional guarantor ${i + 1}`
                              : (mainIdx === i ? "Main borrower — loan is built around them" : `Guarantor ${i + 1}`)}
                          </label>
                          <div className="flex items-center gap-2 shrink-0">
                            {g.borrower_id && (
                              <span className="text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded"
                                data-testid={`guarantor-created-${i}`}>
                                created #{g.borrower_id}
                              </span>
                            )}
                            {g.error && <span className="text-xs text-red-600 max-w-[200px] truncate" title={g.error}>✗ {g.error}</span>}
                            {guarantors.length > 1 && !g.borrower_id && (
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-red-600"
                                onClick={() => removeGuarantor(i)} data-testid={`guarantor-remove-${i}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </div>
                        </div>
                        {!g.borrower_id && <SumsubSync guarantor={g} onFill={(patch) => fillG(i, patch)} idx={i} isAus={isAus} />}
                        <BorrowerFields value={g} onChange={(k, v) => setG(i, k, v)} idx={i} disabled={!!g.borrower_id} isAus={isAus} />
                      </div>
                    ))}

                    <Button variant="outline" size="sm" onClick={addGuarantor} data-testid="add-guarantor-btn">
                      <Plus className="w-3 h-3 mr-1" /> Add another guarantor
                    </Button>

                    {guarantorCount > 1 && (
                      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 flex items-start gap-1.5">
                        <Users className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                        LoanDisk&apos;s API can&apos;t attach guarantors to the loan — once the loan is created, open it in
                        LoanDisk and add the extra borrower(s) under Loan → Guarantors (they&apos;ll be in the borrower list ready to pick).
                      </p>
                    )}
                  </div>
                )}

                <div className="flex justify-end gap-2">
                  <Button onClick={createBorrowers}
                    disabled={busy === "borrower" ||
                      (rowsToCreate.length > 0 && rowsToCreate.some((g) => !g.borrower_business_name)) ||
                      (rowsToCreate.length === 0 && !borrowerId)}
                    data-testid="create-borrowers-btn">
                    {busy === "borrower" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                    {usingExistingMain
                      ? (rowsToCreate.length > 0
                        ? `Use Selected + Create ${rowsToCreate.length} Guarantor(s)`
                        : "Use Selected Borrower")
                      : rowsToCreate.length === 0
                        ? "Continue"
                        : `Create ${rowsToCreate.length} Borrower(s) & Continue`}
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}

            {/* STEP 1: Loan details */}
            {step === 1 && (
              <div className="space-y-4">
                <ProductBasisBlock basis={prefill?.product_basis} loan={loan} setL={setL} products={prefill?.products || []} />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Loan # (SF Number) *</Label>
                    <Input value={loan.loan_application_id || ""} onChange={(e) => setL("loan_application_id", e.target.value)}
                      data-testid="loan-sf-number" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Principal ({cur}) *</Label>
                    <Input type="number" value={loan.loan_principal_amount || ""} onChange={(e) => setL("loan_principal_amount", e.target.value)}
                      data-testid="loan-principal" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Released Date *</Label>
                    <DateField value={loan.loan_released_date || ""} onChange={(v) => setL("loan_released_date", v)}
                      testid="loan-released-date" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Interest Type *</Label>
                    <Select value={loan.loan_interest_type || "fixed"} onValueChange={(v) => setL("loan_interest_type", v)}>
                      <SelectTrigger data-testid="loan-interest-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fixed">Fixed {cur} amount (per loan)</SelectItem>
                        <SelectItem value="percentage">Percentage %</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">
                      Interest {loan.loan_interest_type === "percentage" ? "(%)" : `(${cur} total)`} *
                    </Label>
                    <Input type="number" value={loan.loan_interest ?? ""} onChange={(e) => setL("loan_interest", e.target.value)}
                      data-testid="loan-interest" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Duration (Weeks) *</Label>
                    <Input type="number" value={loan.loan_duration || ""} onChange={(e) => setL("loan_duration", e.target.value)}
                      data-testid="loan-duration" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Repayment Cycle *</Label>
                    <Select value={String(loan.loan_payment_scheme_id || "")} onValueChange={(v) => setL("loan_payment_scheme_id", v)}>
                      <SelectTrigger data-testid="loan-scheme-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {(prefill?.payment_schemes || []).map((s) => (
                          <SelectItem key={s.id} value={String(s.id)}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground"># of Repayments *</Label>
                    <Input type="number" value={loan.loan_num_of_repayments || ""} onChange={(e) => setL("loan_num_of_repayments", e.target.value)}
                      data-testid="loan-num-repayments" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Payment Start Date *</Label>
                    <DateField value={loan.loan_first_repayment_date || ""} onChange={(v) => setL("loan_first_repayment_date", v)}
                      placeholder="dd/mm/yyyy" testid="loan-first-repayment" />
                    {prefill?.first_repayment_source === "gocardless" && (
                      <p className="text-[11px] text-emerald-700 mt-0.5" data-testid="loan-first-repayment-gc-note">
                        Pulled from GoCardless — first collection date on the mandate&apos;s payment cycle.
                      </p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Arrangement Fee ({cur}, optional)</Label>
                    <Input type="number" value={loan.admin_fee ?? ""} onChange={(e) => setL("admin_fee", e.target.value)}
                      data-testid="loan-admin-fee" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Legal Fee ({cur}, optional)</Label>
                    <Input type="number" value={loan.legal_fee ?? ""} onChange={(e) => setL("legal_fee", e.target.value)}
                      data-testid="loan-legal-fee" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Disbursed By</Label>
                    <Select value={String(loan.loan_disbursed_by_id || "")} onValueChange={(v) => setL("loan_disbursed_by_id", v)}>
                      <SelectTrigger data-testid="loan-disbursed-by"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {(prefill?.disbursed_by || []).map((d) => (
                          <SelectItem key={d.id} value={String(d.id)}>{d.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => setStep(0)}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
                  <Button onClick={() => setStep(2)} disabled={!loanFieldsValid} data-testid="loan-review-btn">
                    Review <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}

            {/* STEP 2: Review & create */}
            {step === 2 && (
              <div className="space-y-4">
                <div className="border rounded-lg divide-y text-sm" data-testid="loan-review-table">
                  {[
                    ["Main Borrower ID", borrowerId],
                    ["Guarantor borrowers", guarantors.filter((g) => g.borrower_id && String(g.borrower_id) !== String(borrowerId))
                      .map((g) => `${g.borrower_firstname || ""} ${g.borrower_lastname || ""}`.trim() || g.borrower_business_name)
                      .join(", ") || "—"],
                    ["Product", productLabel(prefill?.products, loan.home_owner, loan.debenture)],
                    ["Loan #", loan.loan_application_id],
                    ["Principal", `${cur}${Number(loan.loan_principal_amount).toLocaleString()}`],
                    ["Interest", loan.loan_interest_type === "percentage" ? `${loan.loan_interest}%` : `${cur}${Number(loan.loan_interest).toLocaleString()} (fixed)`],
                    ["Duration", `${loan.loan_duration} weeks`],
                    ["Cycle", (prefill?.payment_schemes || []).find((s) => String(s.id) === String(loan.loan_payment_scheme_id))?.label],
                    ["Repayments", loan.loan_num_of_repayments],
                    ["Released", loan.loan_released_date],
                    ["Payment start", loan.loan_first_repayment_date],
                    ["Arrangement Fee", loan.admin_fee ? `${cur}${loan.admin_fee} (distributed evenly)` : "—"],
                    ["Legal Fee", loan.legal_fee ? `${cur}${loan.legal_fee} (distributed evenly)` : "—"],
                  ].map(([k, v]) => (
                    <div key={k} className="flex justify-between px-3 py-2">
                      <span className="text-muted-foreground">{k}</span>
                      <span className="font-medium">{v ?? "—"}</span>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => setStep(1)}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
                  <Button onClick={createLoan} disabled={busy === "loan"} className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="create-loan-btn">
                    {busy === "loan" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                    Create Loan in LoanDisk
                  </Button>
                </div>
              </div>
            )}

            {/* STEP 3: Attach DocuSign docs */}
            {step === 3 && (
              <div className="space-y-4">
                <div className="p-3 bg-emerald-50 rounded-lg text-sm text-emerald-800 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" /> Loan <span className="font-mono font-semibold">{createdLoanId}</span> created — now attach the signed documents.
                </div>
                {guarantors.filter((g) => g.borrower_id && String(g.borrower_id) !== String(borrowerId)).length > 0 && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800 flex items-start gap-1.5"
                    data-testid="attach-guarantors-reminder">
                    <Users className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span>
                      Don&apos;t forget: open loan <span className="font-mono">{createdLoanId}</span> in LoanDisk and attach{" "}
                      {guarantors.filter((g) => g.borrower_id && String(g.borrower_id) !== String(borrowerId))
                        .map((g) => `${g.borrower_firstname || ""} ${g.borrower_lastname || ""}`.trim() || g.borrower_business_name)
                        .join(", ")}{" "}
                      as guarantor(s) — their borrower records are ready to pick.
                    </span>
                  </div>
                )}
                {(prefill?.envelopes || []).length === 0 ? (
                  <p className="text-sm text-red-600" data-testid="no-envelopes-warning">
                    No completed DocuSign envelopes found — the signed contracts must be attached to the loan.
                    Close this wizard, refresh the DocuSign stage, then reopen to attach them.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {(prefill.envelopes || []).map((env) => (
                      <label key={env.envelope_id} className="flex items-center gap-2 p-2 border rounded-lg cursor-pointer text-sm">
                        <input type="checkbox" checked={selectedEnvelopes.includes(env.envelope_id)}
                          onChange={(e) => setSelectedEnvelopes((prev) => e.target.checked
                            ? [...prev, env.envelope_id] : prev.filter((id) => id !== env.envelope_id))} />
                        <FileText className="w-4 h-4 text-slate-400" />
                        <span className="flex-1 truncate">{env.email_subject || env.subject || env.envelope_id}</span>
                        <span className="text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">{env.status}</span>
                      </label>
                    ))}
                  </div>
                )}
                {attachResults && (
                  <div className="space-y-1 text-xs" data-testid="attach-results">
                    {attachResults.map((r, i) => (
                      <p key={i} className={r.status === "success" ? "text-emerald-700" : "text-red-600"}>
                        {r.status === "success" ? "✓" : "✗"} {r.filename || r.envelope_id} {r.message && `— ${r.message}`}
                      </p>
                    ))}
                  </div>
                )}
                <div className="flex justify-end gap-2">
                  {(prefill?.envelopes || []).length > 0 && !attachResults && (
                    <Button onClick={attachDocs} disabled={busy === "docs" || selectedEnvelopes.length === 0}
                      data-testid="attach-docs-btn">
                      {busy === "docs" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                      Attach {selectedEnvelopes.length} File(s)
                    </Button>
                  )}
                  <Button variant={attachResults ? "default" : "outline"} onClick={finish}
                    disabled={(prefill?.envelopes || []).length > 0 && !attachResults}
                    title={(prefill?.envelopes || []).length > 0 && !attachResults
                      ? "Attach the signed documents first — they go up together with the loan" : undefined}
                    data-testid="wizard-finish-btn">
                    Finish
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
